import type { Item } from '@mimir/common/dto/searchResultHit';
import type { SearchItemType } from '@mimir/common/dto/itemTypes';
import { ItemType } from '@mimir/common/dto/itemTypes';
import { URL_ROOT } from '../configConstants';
import isEmpty from 'lodash/isEmpty';
import { isPremiereProjectFile } from '../premiere/misc';
import { assertUnreachable } from '@mimir/common/util/general';
import { isPlaceholderItem } from '@mimir/common/util/item';

const staticPlaceholderThumbnailFiles = {
  [ItemType.VIDEO]: 'mimir-placeholder-video.png',
  [ItemType.CLIP_LIST]: 'mimir-placeholder-mseq.png',
  [ItemType.TIMELINE]: 'mimir-placeholder-mseq.png',
  [ItemType.AUDIO]: 'mimir-placeholder-audio.png',
  [ItemType.IMAGE]: 'image-placeholder.png',
  [ItemType.FILE]: 'mimir-placeholder-file.png',
  [ItemType.FOLDER]: 'folder-1024.png',
  prproj: 'mimir-placeholder-prproj.png',
  default: 'missing-item.png',
};

export function doesItemHavePlaceholderThumbnail(item: Item): boolean {
  return !!(item.hasOwnProperty('resolvedThumbnailUrl') && Object.values(staticPlaceholderThumbnailFiles).find(f => item.resolvedThumbnailUrl?.endsWith(f)));
}

export function getPlaceholderThumbBorderClass(item: Item): string {
  if (!isPlaceholderItem(item) || !doesItemHavePlaceholderThumbnail(item)) return '';
  if (item.resolvedThumbnailUrl?.endsWith(staticPlaceholderThumbnailFiles.prproj)) return '';
  switch (item.itemType) {
    case ItemType.VIDEO: return 'ph-thumb-border-video';
    case ItemType.AUDIO: return 'ph-thumb-border-audio';
    case ItemType.IMAGE: return 'ph-thumb-border-image';
    case ItemType.FILE: return 'ph-thumb-border-file';
    case ItemType.FOLDER: return 'ph-thumb-border-folder';
    case ItemType.CLIP_LIST: return 'ph-thumb-border-cliplist';
    case ItemType.TIMELINE: return 'ph-thumb-border-timeline';
    case ItemType.PERSON: return 'ph-thumb-border-person';
  }
}

export const getThumbUrl = (url: string, itemType?: SearchItemType, fileName?: string) => {
  const constructStaticThumbnailPath = (thumbnailFileName: string) => {
    return `${URL_ROOT}assets/${thumbnailFileName}`;
  };

  // We will never receive thumbnails for folder, so we return early.
  if (itemType === ItemType.FOLDER) {
    return constructStaticThumbnailPath(staticPlaceholderThumbnailFiles[ItemType.FOLDER]);
  }

  // If we have a url, use it.
  if (!isEmpty(url)) {
    return url;
  }

  if (fileName && isPremiereProjectFile(fileName)) {
    return constructStaticThumbnailPath(staticPlaceholderThumbnailFiles.prproj);
  }

  if (itemType === ItemType.FILE) {
    return constructStaticThumbnailPath(staticPlaceholderThumbnailFiles[ItemType.FILE]);
  }


  // Audio fallback in case there is no url
  if (itemType === ItemType.AUDIO) {
    return constructStaticThumbnailPath(staticPlaceholderThumbnailFiles[ItemType.AUDIO]);
  }

  if (itemType === ItemType.VIDEO) {
    return constructStaticThumbnailPath(staticPlaceholderThumbnailFiles[ItemType.VIDEO]);
  }

  if (itemType === ItemType.IMAGE) {
    return constructStaticThumbnailPath(staticPlaceholderThumbnailFiles[ItemType.IMAGE]);
  }

  if (itemType === ItemType.CLIP_LIST) {
    return constructStaticThumbnailPath(staticPlaceholderThumbnailFiles[ItemType.CLIP_LIST]);
  }

  if (itemType === ItemType.TIMELINE) {
    return constructStaticThumbnailPath(staticPlaceholderThumbnailFiles[ItemType.TIMELINE]);
  }

  if (itemType === ItemType.PERSON) {
    return constructStaticThumbnailPath(staticPlaceholderThumbnailFiles.default);
  }

  if (itemType === 'graphicsTemplate') {
    console.log('graphicsTemplate item type detected, using file placeholder thumbnail');
    return constructStaticThumbnailPath(staticPlaceholderThumbnailFiles[ItemType.FILE]);
  }

  if (itemType) {
    assertUnreachable(itemType);
  }
  // Fallback to placeholder thumbnail.
  return constructStaticThumbnailPath(staticPlaceholderThumbnailFiles.default);
};

/**
 * Appends an ampersand to the URL to prevent a strange CORS issue in Chrome.
 * This issue occurs when a <v-img> tag fetches a thumbnail, and other tags or
 * code try to fetch the same URL, leading to a CORS conflict.
 *
 * Issues:
 *  1. In the player code, the <video> tag tries to fetch the poster frame with crossorigin="anonymous",
 *     but <v-img> has already fetched and cached the same URL differently (since <v-img> does not obey
 *     crossorigin attribute like <img> does, and renders in a <div> ultimately).
 *  2. In the attachments tab, a preview is shown using a <v-img> tag for images. When trying to download
 *     the attachment from Mimir desktop, the same URL is used, causing a CORS issue.
 *
 * Similar issues:
 * - https://support.google.com/chrome/thread/76617220?hl=en
 * - https://bugs.chromium.org/p/chromium/issues/detail?id=409090
 *
 * @returns URL with an appended ampersand.
 */
export const avoidCORSConflict = (url: string) => {
  return `${url}&`;
};
